/**
 * Clamp keyboard nudge targets to the same main / off-canvas bounds used during drag
 * (dragBoundFunc + constrainOffCanvasPosition).
 */
import { constrainOffCanvasPosition, getSimpleBoundingBox } from "./dragEndUtils";

function mockStage(width, height) {
  return {
    width: () => width,
    height: () => height,
  };
}

/**
 * Line / arrow / double-arrow group position: keep absolute geometry inside the stage rect
 * (matches constrainOffCanvasPosition line branch, without main-column avoidance — not used on main).
 */
function constrainLineLikeGroupToStage(element, newX, newY, stageWidth, stageHeight) {
  const points = element.points || [0, 0, 0, 0];
  if (points.length < 4) {
    return { x: newX, y: newY };
  }

  let minPointX = Infinity;
  let minPointY = Infinity;
  let maxPointX = -Infinity;
  let maxPointY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minPointX = Math.min(minPointX, points[i]);
    maxPointX = Math.max(maxPointX, points[i]);
    minPointY = Math.min(minPointY, points[i + 1]);
    maxPointY = Math.max(maxPointY, points[i + 1]);
  }

  const strokeWidth = element.strokeWidth || 2;
  const padding = Math.max(strokeWidth / 2, 5);

  const absMinX = minPointX + newX;
  const absMaxX = maxPointX + newX;
  const absMinY = minPointY + newY;
  const absMaxY = maxPointY + newY;

  let deltaX = 0;
  let deltaY = 0;
  if (absMinX < padding) {
    deltaX = padding - absMinX;
  } else if (absMaxX > stageWidth - padding) {
    deltaX = stageWidth - padding - absMaxX;
  }
  if (absMinY < padding) {
    deltaY = padding - absMinY;
  } else if (absMaxY > stageHeight - padding) {
    deltaY = stageHeight - padding - absMaxY;
  }

  return { x: newX + deltaX, y: newY + deltaY };
}

function constrainMainCanvasPosition(element, elementType, newX, newY, stageWidth, stageHeight) {
  if (
    elementType === "Line" ||
    elementType === "Arrow" ||
    elementType === "DoubleSidedArrow"
  ) {
    return constrainLineLikeGroupToStage(element, newX, newY, stageWidth, stageHeight);
  }

  const bbox = getSimpleBoundingBox(element, elementType);
  if (bbox.isCenterCoordinate) {
    const halfW = bbox.width / 2;
    const halfH = bbox.height / 2;
    return {
      x: Math.max(halfW, Math.min(stageWidth - halfW, newX)),
      y: Math.max(halfH, Math.min(stageHeight - halfH, newY)),
    };
  }

  const w = bbox.width || 0;
  const h = bbox.height || 0;
  return {
    x: Math.max(0, Math.min(stageWidth - w, newX)),
    y: Math.max(0, Math.min(stageHeight - h, newY)),
  };
}

/**
 * @param {Object} constraintOptions
 * @param {number} [constraintOptions.mainStageWidth]
 * @param {number} [constraintOptions.mainStageHeight]
 * @param {number} [constraintOptions.offCanvasStageWidth]
 * @param {number} [constraintOptions.offCanvasStageHeight]
 */
export function constrainNudgePosition(
  element,
  elementType,
  newX,
  newY,
  isMainCanvas,
  constraintOptions = {},
) {
  const {
    mainStageWidth,
    mainStageHeight,
    offCanvasStageWidth,
    offCanvasStageHeight,
  } = constraintOptions;

  const mw =
    typeof mainStageWidth === "number" && Number.isFinite(mainStageWidth)
      ? mainStageWidth
      : window.innerWidth * 0.45;
  const mh =
    typeof mainStageHeight === "number" && Number.isFinite(mainStageHeight)
      ? mainStageHeight
      : window.innerHeight;
  const ow =
    typeof offCanvasStageWidth === "number" && Number.isFinite(offCanvasStageWidth)
      ? offCanvasStageWidth
      : window.innerWidth;
  const oh =
    typeof offCanvasStageHeight === "number" && Number.isFinite(offCanvasStageHeight)
      ? offCanvasStageHeight
      : window.innerHeight;

  if (isMainCanvas) {
    return constrainMainCanvasPosition(element, elementType, newX, newY, mw, mh);
  }

  return constrainOffCanvasPosition(
    element,
    elementType,
    newX,
    newY,
    mockStage(ow, oh),
  );
}
